import type { CarnivalGameState, Die } from "../types";
import { TOTAL_ROUNDS } from "../types";
import {
  computeRoundSummary,
  determineWinners,
  revealOrderFrom,
  rollAllDice,
} from "../utils";

/**
 * Distributes all still-unlocked black dice to the round's Pride revealers,
 * alternating in their original reveal order, starting from the first to
 * reveal Pride. No-op if nobody played Pride.
 */
function distributePride(
  dice: readonly Die[],
  prideRevealerIds: readonly string[],
): readonly Die[] {
  if (prideRevealerIds.length === 0) return dice;
  let takenSoFar = 0;
  return dice.map((die) => {
    if (die.color !== "black" || die.lockedBy !== null) return die;
    const ownerId = prideRevealerIds[takenSoFar % prideRevealerIds.length];
    takenSoFar += 1;
    return { ...die, lockedBy: { ownerId, sin: "pride" as const } };
  });
}

/**
 * Runs once the last player of the round has revealed: resolves Pride,
 * scores the round, hands off the devil card, and either rolls the next
 * round or ends the game.
 */
export function cascadeRoundEnd(state: CarnivalGameState): CarnivalGameState {
  const prideRevealerIds = state.revealedThisRound
    .filter((record) => record.sin === "pride")
    .sort((a, b) => a.revealIndex - b.revealIndex)
    .map((record) => record.playerId);
  const diceAfterPride = distributePride(state.dice, prideRevealerIds);
  const stateForScoring: CarnivalGameState = { ...state, dice: diceAfterPride };
  const summary = computeRoundSummary(stateForScoring);

  const scoreHistoryByPlayerId = Object.fromEntries(
    state.players.map((player) => [
      player.playerId,
      [
        ...(state.scoreHistoryByPlayerId[player.playerId] ?? []),
        summary.finalScoreByPlayerId[player.playerId],
      ],
    ]),
  );

  const scoredState: CarnivalGameState = {
    ...stateForScoring,
    scoreHistoryByPlayerId,
    lastRoundSummary: summary,
    devilCardHolderId: summary.nextDevilCardHolderId,
  };

  if (state.round >= TOTAL_ROUNDS) {
    return {
      ...scoredState,
      phase: "GAME_OVER",
      winnerIds: determineWinners(scoredState),
    };
  }

  const roll = rollAllDice(scoredState.rngState);
  const nextRound = scoredState.round + 1;
  const revealOrder = revealOrderFrom(
    scoredState.seatingOrder,
    scoredState.devilCardHolderId,
  );

  return {
    ...scoredState,
    phase: "SECRET_SELECT",
    round: nextRound,
    dice: roll.dice,
    rngState: roll.rngState,
    revealOrder,
    nextToRevealIndex: 0,
    selectionsByPlayerId: Object.fromEntries(
      state.players.map((player) => [player.playerId, null]),
    ),
    revealedThisRound: [],
  };
}
